import type {
  JobContent,
  JobContentBase,
  JobContentTotals,
} from '@payaca/types/jobContentTypes';
import type { JobPayment } from '@payaca/types/jobPaymentTypes';
import type { JobType } from '@payaca/types/jobTypes';
import { JobStatus, ReadableJobStatus } from '@payaca/types/jobTypes';
import type { BaseJob, Job } from '@payaca/types/jobTypesV2';
import {
  getCompletedDepositPaymentValueFromJobPayments,
  getTotalCompletedPaymentValueFromJobPayments,
} from './jobPaymentHelper';
import {
  isEstimate,
  isInvoice,
  isQuote,
  isQuoteOrEstimate,
  isSentQuoteEstimate,
  jobHasSent,
} from './jobStatusHelper';

import type { Customer } from '@payaca/types/customerTypes';
import type { PrelineColourVariant } from '@payaca/types/preline';
import { getJobContactFromCustomer } from './customerHelper';
import { StatusBadgeState } from './statusBadgeHelper';

export const isDepositPaymentRequired = (
  job: BaseJob,
  jobContent: JobContentBase
): boolean => {
  return (
    isQuoteOrEstimate(job.status) &&
    (!!jobContent.depositAmount || !!jobContent.depositPercentage)
  );
};

export const isInvoicePaymentRequired = (
  job: BaseJob,
  jobContentTotals: JobContentTotals
): boolean => {
  return isInvoice(job.status) && jobContentTotals.total > 0;
};

export const isDepositConfirmedPaid = (
  jobContentTotals: JobContentTotals,
  jobPayments: JobPayment[]
): boolean => {
  const completedDepositPaymentValue =
    getCompletedDepositPaymentValueFromJobPayments(jobPayments);
  const remainingDeposit =
    (jobContentTotals.calculatedDepositAmount || 0) -
    completedDepositPaymentValue;
  return remainingDeposit <= 0;
};

export const isInvoiceConfirmedPaid = (
  jobContentTotals: JobContentTotals,
  jobPayments: JobPayment[]
): boolean => {
  const attemptedPaymentValue =
    getTotalCompletedPaymentValueFromJobPayments(jobPayments);
  const remainingTotal = jobContentTotals.total - attemptedPaymentValue;
  return remainingTotal <= 0;
};

type JobDecisionFields = Pick<Job, 'status'> & {
  acceptedAt?: Date | null;
  declinedAt?: Date | null;
};

export const isJobAccepted = (job: JobDecisionFields): boolean => {
  return isQuoteOrEstimate(job.status) && !!job.acceptedAt;
};

export const isJobDeclined = (job: JobDecisionFields): boolean => {
  return isQuoteOrEstimate(job.status) && !!job.declinedAt;
};

export const canMarkJobAsAccepted = (job: Job): boolean => {
  return (
    isSentQuoteEstimate(job.status) &&
    !isJobAccepted(job) &&
    !isJobDeclined(job)
  );
};

export const canMarkJobAsDeclined = (job: JobDecisionFields): boolean => {
  return (
    isSentQuoteEstimate(job.status) &&
    !isJobDeclined(job) &&
    !isJobAccepted(job)
  );
};

export const getJobStatusBadgeState = (status?: ReadableJobStatus) => {
  switch (status) {
    case ReadableJobStatus.ACCEPTED:
    case ReadableJobStatus.DEPOSIT_PAID:
    case ReadableJobStatus.PAID:
      return StatusBadgeState.GREEN_SUCCESS;
    case ReadableJobStatus.DEPOSIT_DUE:
    case ReadableJobStatus.PAYMENT_PENDING:
      return StatusBadgeState.AMBER_PENDING;
    case ReadableJobStatus.DECLINED:
    case ReadableJobStatus.EXPIRED:
    case ReadableJobStatus.BOUNCED:
    case ReadableJobStatus.OVERDUE:
      return StatusBadgeState.RED_FAILURE;
    case ReadableJobStatus.ARCHIVED:
    case ReadableJobStatus.INACTIVE:
      return StatusBadgeState.GHOST;
    case ReadableJobStatus.DRAFT:
      return StatusBadgeState.GREY_INITIAL;
    default:
      return StatusBadgeState.BLUE_NEUTRAL;
  }
};

export const getJobStatusPLBadgeState = (
  status?: ReadableJobStatus
): PrelineColourVariant => {
  switch (status) {
    case ReadableJobStatus.ACCEPTED:
    case ReadableJobStatus.DEPOSIT_PAID:
    case ReadableJobStatus.PAID:
      return 'green';
    case ReadableJobStatus.DEPOSIT_DUE:
    case ReadableJobStatus.PAYMENT_PENDING:
      return 'yellow';
    case ReadableJobStatus.DECLINED:
    case ReadableJobStatus.EXPIRED:
    case ReadableJobStatus.BOUNCED:
    case ReadableJobStatus.OVERDUE:
      return 'red';
    case ReadableJobStatus.ARCHIVED:
    case ReadableJobStatus.INACTIVE:
      return 'black';
    default:
      return 'blue';
  }
};

export const getJobType = (job: Pick<BaseJob, 'status'>): JobType => {
  if (isInvoice(job.status)) {
    return 'Invoice';
  } else if (isQuote(job.status)) {
    return 'Quote';
  } else if (isEstimate(job.status)) {
    return 'Estimate';
  } else {
    return 'Job';
  }
};

export const canResendJob = (
  job: Job,
  customer: Customer,
  hasRelatedJobInformation: boolean
): boolean => {
  const primaryContact = getJobContactFromCustomer(customer, job.contactId);

  return (
    jobHasSent(job.status) &&
    !job.archivedAt &&
    (isQuote(job.status) ? !hasRelatedJobInformation : true) &&
    !!primaryContact?.emailAddress
  );
};

export const canEditSentJob = (
  job: Pick<BaseJob, 'status'> & { acceptedAt?: Date | null },
  jobPayments: JobPayment[],
  hasRelatedJobInformation: boolean
): boolean => {
  const nonFailedPayments = jobPayments?.filter(
    (x) => !x.paymentFailedConfirmationAt
  );

  if (nonFailedPayments?.length) {
    return false;
  }
  if (isSentQuoteEstimate(job.status)) {
    return !hasRelatedJobInformation && !job.acceptedAt;
  } else if (job.status === JobStatus.INVOICED) {
    return true;
  }
  return false;
};

export const canUnacceptProposal = (
  job: Job,
  jobPayments?: JobPayment[],
  hasRelatedProposalsOrInvoices?: boolean
): boolean => {
  return (
    isSentQuoteEstimate(job.status) &&
    !!job.acceptedAt &&
    !jobPayments?.length &&
    !hasRelatedProposalsOrInvoices
  );
};

export const getOutstandingPaymentValue = (
  job: Job,
  jobContent: JobContent,
  jobPayments: JobPayment[]
): number | null => {
  let value = null;

  const jobIsInvoice = isInvoice(job.status);

  if (jobIsInvoice && !!job.sentAt) {
    value =
      jobContent.total -
      getTotalCompletedPaymentValueFromJobPayments(jobPayments);
  } else if (!jobIsInvoice && !!job.acceptedAt) {
    value =
      (jobContent.calculatedDepositAmount || 0) -
      getCompletedDepositPaymentValueFromJobPayments(jobPayments);
  }

  if (value === null) return value;
  return value < 0 ? 0 : Math.ceil(value);
};

export const canDuplicateJob = (job: {
  isPartialInvoice?: boolean | null;
}): boolean => {
  return !job.isPartialInvoice;
};
